import { selectionIsLocked } from '@/types/selectionWorkflow'
import { InvitationStatus } from './InvitationStatus'
import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  CURATION_COLUMNS,
  type CurationColumnId,
  type Submission,
} from '../hooks/useCurationSubmissions'

interface Props {
  grouped: Map<CurationColumnId, Submission[]>
  onMove: (submissionId: string, toColumn: CurationColumnId, toIndex: number) => void
  onNotesChange: (submissionId: string, notes: string) => void
  onOpenDetail: (submission: Submission) => void
}

const ACCENT_DOT: Record<string, string> = {
  neutral: 'bg-muted-foreground',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  destructive: 'bg-destructive',
}

/**
 * Sectioned list. Section config is identical to the Board view's column config
 * so changes propagate to both views from one source of truth.
 */
export function CurationListView({ grouped, onMove, onNotesChange, onOpenDetail }: Props) {
  return (
    <div className="space-y-2">
      {CURATION_COLUMNS.map((col) => {
        const cards = grouped.get(col.id) ?? []
        return (
          <Section
            key={col.id}
            title={col.title}
            count={cards.length}
            accent={col.accent}
            ordered={col.ordered}
          >
            {cards.map((s, i) => (
              <Row
                key={s.id}
                submission={s}
                rankIndex={col.ordered ? i + 1 : undefined}
                currentColumn={col.id}
                onMove={onMove}
                onNotesChange={(n) => onNotesChange(s.id, n)}
                onOpenDetail={() => onOpenDetail(s)}
              />
            ))}
            {cards.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-muted-foreground">Empty</div>
            )}
          </Section>
        )
      })}
    </div>
  )
}

function Section({
  title,
  count,
  accent,
  children,
}: {
  title: string
  count: number
  accent: string
  ordered: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(true)
  return (
    <div className="overflow-hidden rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 bg-muted/40 px-3 py-2 text-left text-sm font-semibold hover:bg-muted/60"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className={`inline-block h-2 w-2 rounded-full ${ACCENT_DOT[accent]}`} />
        {title}
        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
          {count}
        </span>
      </button>
      {open && <div className="divide-y">{children}</div>}
    </div>
  )
}

function Row({
  submission,
  rankIndex,
  currentColumn,
  onMove,
  onNotesChange,
  onOpenDetail,
}: {
  submission: Submission
  rankIndex?: number
  currentColumn: CurationColumnId
  onMove: (submissionId: string, toColumn: CurationColumnId, toIndex: number) => void
  onNotesChange: (next: string) => void
  onOpenDetail: () => void
}) {
  const [notesEditing, setNotesEditing] = useState(false)
  const [draft, setDraft] = useState(submission.curatorNotes ?? '')
  const fullName =
    `${submission.applicantInfo.firstName} ${submission.applicantInfo.lastName}`.trim()

  function submittedAt() {
    const ms = submission.submittedAt?.toMillis?.() ?? 0
    if (!ms) return ''
    const d = new Date(ms)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        // Avoid triggering when clicking inside notes editor or status dropdown
        const target = e.target as HTMLElement
        if (target.closest('input,textarea,[role="combobox"],[role="listbox"]')) return
        onOpenDetail()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpenDetail()
        }
      }}
      className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/40"
    >
      {rankIndex !== undefined && (
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
          {rankIndex}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{fullName || 'Unknown applicant'}</p>
        <p className="truncate text-xs text-muted-foreground">
          {submission.applicantInfo.email} · {submittedAt()}
        </p>
        <InvitationStatus submission={submission} />
      </div>
      <div className="hidden min-w-0 flex-1 sm:block">
        {notesEditing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              setNotesEditing(false)
              if (draft !== (submission.curatorNotes ?? '')) onNotesChange(draft)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              if (e.key === 'Escape') {
                setDraft(submission.curatorNotes ?? '')
                setNotesEditing(false)
              }
            }}
            placeholder="Curator notes…"
            className="w-full rounded border bg-background px-2 py-1 text-xs"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraft(submission.curatorNotes ?? '')
              setNotesEditing(true)
            }}
            className="w-full truncate rounded border border-dashed border-transparent px-2 py-1 text-left text-xs text-muted-foreground hover:border-border"
          >
            {submission.curatorNotes || <span className="italic">Add notes…</span>}
          </button>
        )}
      </div>
      <Select
        value={currentColumn}
        disabled={selectionIsLocked(submission)}
        onValueChange={(v) => onMove(submission.id, v as CurationColumnId, 0)}
      >
        <SelectTrigger className="h-8 w-32 shrink-0 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CURATION_COLUMNS.map((c) => (
            <SelectItem key={c.id} value={c.id} className="text-xs">
              {c.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
